import React, { useCallback, useLayoutEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import MediaControl from "./MediaControl";
import TableControl from "./TableControl";
import {
  ARTICLE_LOCALES,
  HEADING_LEVELS,
  createClientBlock,
  createLocaleContent,
  getLocaleContent,
  isBilingualBlockType,
} from "../articleApi";
import { BUTTON_TARGET_OPTIONS } from "./BlockToolbar";

const AutoResizeTextarea = ({ className, value, placeholder, onChange }) => {
  const textareaRef = useRef(null);

  const resizeTextarea = useCallback((element) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    resizeTextarea(textareaRef.current);
  }, [value, resizeTextarea]);

  return (
    <textarea
      ref={textareaRef}
      className={className}
      value={value}
      placeholder={placeholder}
      rows={1}
      onChange={(event) => {
        onChange(event);
        resizeTextarea(event.target);
      }}
    />
  );
};

const nestedTypes = [
  { type: "paragraph", label: "Paragraph" },
  { type: "heading", label: "Heading" },
  { type: "image", label: "Image" },
  { type: "button", label: "Button" },
];

const localePlaceholder = (locale, english, chinese) =>
  locale === "chinese" ? chinese : english;

const BilingualFields = ({ children }) => (
  <div className="gutenberg-bilingual">
    {ARTICLE_LOCALES.map((locale) => (
      <div key={locale.value} className={`gutenberg-locale-box gutenberg-locale-box--${locale.value}`}>
        <span className="gutenberg-locale-box__label">{locale.label}</span>
        <div className="gutenberg-locale-box__body">{children(locale.value)}</div>
      </div>
    ))}
  </div>
);

const NestedOptionSelect = ({ value, onChange, children }) => (
  <select
    className="gutenberg-block__option"
    value={value}
    onClick={(event) => event.stopPropagation()}
    onChange={(event) => {
      event.stopPropagation();
      onChange(event.target.value);
    }}
  >
    {children}
  </select>
);

const BlockRenderer = ({ block, onUpdate, articleId = null, showBlockOptions = true }) => {
  const patchLocale = (locale, patch, extras = {}) => {
    const current = createLocaleContent(block.data?.[locale], extras);
    onUpdate({ [locale]: { ...current, ...patch } });
  };

  if (block.type === "heading") {
    const HeadingTag = block.data.level || "h2";
    return (
      <div className="gutenberg-block__stack">
        {showBlockOptions && (
          <NestedOptionSelect
            value={block.data.level || "h2"}
            onChange={(level) => onUpdate({ level })}
          >
            {HEADING_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level.toUpperCase()}
              </option>
            ))}
          </NestedOptionSelect>
        )}
        <BilingualFields>
          {(locale) => {
            const localeData = getLocaleContent(block.data, locale);
            return (
              <HeadingTag className="gutenberg-block__heading">
                <AutoResizeTextarea
                  value={localeData.content || ""}
                  placeholder={localePlaceholder(locale, "Heading", "标题")}
                  onChange={(event) => patchLocale(locale, { content: event.target.value })}
                />
              </HeadingTag>
            );
          }}
        </BilingualFields>
      </div>
    );
  }

  if (block.type === "paragraph" || block.type === "text") {
    return (
      <BilingualFields>
        {(locale) => {
          const localeData = getLocaleContent(block.data, locale);
          return (
            <AutoResizeTextarea
              className="gutenberg-block__paragraph"
              value={localeData.content || ""}
              placeholder={localePlaceholder(locale, "Type here to choose a block...", "在此输入内容...")}
              onChange={(event) => patchLocale(locale, { content: event.target.value })}
            />
          );
        }}
      </BilingualFields>
    );
  }

  if (block.type === "image") {
    return <MediaControl value={block.data} onChange={onUpdate} articleId={articleId} />;
  }

  if (block.type === "quote") {
    return (
      <BilingualFields>
        {(locale) => {
          const localeData = getLocaleContent(block.data, locale);
          return (
            <blockquote className="gutenberg-block__quote">
              <AutoResizeTextarea
                value={localeData.content || ""}
                placeholder={localePlaceholder(locale, "Write quote...", "输入引言...")}
                onChange={(event) => patchLocale(locale, { content: event.target.value }, { author: "" })}
              />
              <input
                value={localeData.author || ""}
                placeholder={localePlaceholder(locale, "Author", "作者")}
                onChange={(event) => patchLocale(locale, { author: event.target.value }, { author: "" })}
              />
            </blockquote>
          );
        }}
      </BilingualFields>
    );
  }

  if (block.type === "button") {
    return (
      <div className="gutenberg-block__stack">
        {showBlockOptions && (
          <NestedOptionSelect
            value={block.data.target || "_self"}
            onChange={(target) => onUpdate({ target })}
          >
            {BUTTON_TARGET_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NestedOptionSelect>
        )}
        <BilingualFields>
        {(locale) => {
          const localeData = getLocaleContent(block.data, locale);
          return (
            <div className="gutenberg-block__button-wrap">
              <input
                value={localeData.content || ""}
                placeholder={localePlaceholder(locale, "Button text", "按钮文字")}
                onChange={(event) => patchLocale(locale, { content: event.target.value }, { url: "#" })}
              />
              <input
                value={localeData.url || ""}
                placeholder={localePlaceholder(locale, "Button URL", "按钮链接")}
                onChange={(event) => patchLocale(locale, { url: event.target.value }, { url: "#" })}
              />
              <a
                href={localeData.url || "#"}
                className="gutenberg-block__button"
                target={block.data.target || "_self"}
                rel="noreferrer"
              >
                {localeData.content || localePlaceholder(locale, "Read More", "阅读更多")}
              </a>
            </div>
          );
        }}
      </BilingualFields>
      </div>
    );
  }

  if (block.type === "columns") {
    const updateNestedBlock = (columnIndex, nestedBlockId, nextData) => {
      const nextColumns = (block.data.columns || []).map((column, currentColumnIndex) => {
        if (currentColumnIndex !== columnIndex) return column;
        return {
          ...column,
          blocks: column.blocks.map((nestedBlock) =>
            nestedBlock.id === nestedBlockId
              ? { ...nestedBlock, data: { ...nestedBlock.data, ...nextData } }
              : nestedBlock
          ),
        };
      });
      onUpdate({ columns: nextColumns });
    };

    const addNestedBlock = (columnIndex, type = "paragraph") => {
      const nextColumns = (block.data.columns || []).map((column, currentColumnIndex) => {
        if (currentColumnIndex !== columnIndex) return column;
        return { ...column, blocks: [...column.blocks, createClientBlock(type)] };
      });
      onUpdate({ columns: nextColumns });
    };

    const removeNestedBlock = (columnIndex, nestedBlockId) => {
      const nextColumns = (block.data.columns || []).map((column, currentColumnIndex) => {
        if (currentColumnIndex !== columnIndex) return column;
        return {
          ...column,
          blocks: column.blocks.filter((nestedBlock) => nestedBlock.id !== nestedBlockId),
        };
      });
      onUpdate({ columns: nextColumns });
    };

    return (
      <div className="gutenberg-columns">
        {(block.data.columns || []).map((column, columnIndex) => (
          <div className="gutenberg-columns__column" key={columnIndex}>
            <div className="gutenberg-columns__label">Column {columnIndex + 1}</div>
            {(column.blocks || []).map((nestedBlock) => (
              <div
                className={`gutenberg-columns__nested${isBilingualBlockType(nestedBlock.type) ? " is-bilingual" : ""}`}
                key={nestedBlock.id}
              >
                <button
                  type="button"
                  className="gutenberg-columns__remove"
                  onClick={() => removeNestedBlock(columnIndex, nestedBlock.id)}
                >
                  <Icon icon="ph:x" height={12} />
                </button>
                <BlockRenderer
                  block={nestedBlock}
                  articleId={articleId}
                  onUpdate={(nextData) => updateNestedBlock(columnIndex, nestedBlock.id, nextData)}
                />
              </div>
            ))}
            <div className="gutenberg-columns__actions">
              {nestedTypes.map((item) => (
                <button
                  type="button"
                  key={item.type}
                  onClick={() => addNestedBlock(columnIndex, item.type)}
                >
                  + {item.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "divider") {
    return <hr className="gutenberg-block__divider" />;
  }

  if (block.type === "video") {
    return (
      <div className="gutenberg-block__video">
        <input
          value={block.data.url || ""}
          placeholder="Video URL"
          onChange={(event) => onUpdate({ url: event.target.value })}
        />
        {block.data.url && (
          <video controls>
            <source src={block.data.url} />
          </video>
        )}
      </div>
    );
  }

  if (block.type === "embed") {
    return (
      <AutoResizeTextarea
        className="gutenberg-block__code"
        value={block.data.embed_code || ""}
        placeholder="Paste embed code"
        onChange={(event) => onUpdate({ embed_code: event.target.value })}
      />
    );
  }

  if (block.type === "html") {
    return (
      <BilingualFields>
        {(locale) => {
          const localeData = getLocaleContent(block.data, locale);
          return (
            <textarea
              className="gutenberg-block__code"
              value={localeData.content || ""}
              placeholder={localePlaceholder(locale, "Custom HTML", "自定义 HTML")}
              rows={5}
              onChange={(event) => patchLocale(locale, { content: event.target.value })}
            />
          );
        }}
      </BilingualFields>
    );
  }

  if (block.type === "table") {
    return <TableControl blockId={block.id} value={block.data} onChange={onUpdate} />;
  }

  return null;
};

export default BlockRenderer;
